import React, { FunctionComponent, useState, useEffect, useRef } from "react";
import { Select } from "antd";
import {
    PolygonNameWithCircle,
    RechartBarLine,
    ChartBodyWrapper,
    ChartButtonWrapper,
    ReportDataWrapper,
    formatYValue,
    ChartTitle,
    shortenStr,
    formatLabelWithEllipsis,
} from "../../../Common";
import {
    isOutputSelected,
    registerElement,
    registerRef,
} from "../../../Common/DigitalShare/DigitalShareGenerator";
import { featureFlags } from "../../../../Common/CurrentFeatureFlags";
import {
    colorTwoBright,
    colorTwoDark,
} from "../../../../globalStyles/colors/scoutColors";
import {
    RenderType,
    DropdownComparisonWeight,
    DigitalShareAppType,
    DigitalShareItemType,
} from "../../../../Enums/Enums";
import {
    IChartVisitorOrigData,
    IPolygon,
    IChartMergedDatapoint,
    IChartCompare,
    IGPSChartMergedDatapoint,
} from "../../../../Models/Models";
import { IMobilyticsBenchmarkItem } from "../../../../Models/IMobilyticsBenchmarkItem";
import MobilyticsPolygonSelect from "../MobilyticsPolygonSelect";

interface IGPSVisitOrigChart {
  storeVisitOrigData: IChartVisitorOrigData;
  benchmarkVisitOrigData: IChartVisitorOrigData;
  polygons: IPolygon[];
  renderType: RenderType;
  benchmarkItems: IMobilyticsBenchmarkItem[];
}

export const GPSVisitOrig: FunctionComponent<IGPSVisitOrigChart> = (
    props: IGPSVisitOrigChart
) => {
    const { polygons, storeVisitOrigData, benchmarkVisitOrigData } = props;

    // ███████╗████████╗ █████╗ ████████╗███████╗
    // ██╔════╝╚══██╔══╝██╔══██╗╚══██╔══╝██╔════╝
    // ███████╗   ██║   ███████║   ██║   █████╗
    // ╚════██║   ██║   ██╔══██║   ██║   ██╔══╝
    // ███████║   ██║   ██║  ██║   ██║   ███████╗
    // ╚══════╝   ╚═╝   ╚═╝  ╚═╝   ╚═╝   ╚══════╝

    const [visitOrigGeogType, setVisitOrigGeogType] = useState<string>("zipcode");
    const [mergedVisitOrigGPSData, setMergedVisitOrigGPSData]
    = useState<IChartMergedDatapoint[]>(null);
    const [polygonIdentifiers, setPolyIdentifiers] = useState<
    { name: string; identifier: string }[]
  >([]);
    const [polygonDataType, setPolygonDataType] = useState<string>("");
    const [originationData, setOriginationData] = useState<
    Map<string, IChartVisitorOrigData>
  >(new Map());
    const [selectedPolygon, setSelectedPolygon] = useState<string>("");
    const [bar1ValueAttr, setBar1ValueAttr] = useState<
    "selection1PrimaryWeight" | "selection1SecondaryWeight" | "nationalWeight"
  >("selection1PrimaryWeight");
    const [dropdownValue, setDropdownValue] = useState<number>(
        DropdownComparisonWeight.PRIMARY
    );
    const [visibleModal, setVisibleModal] = useState<boolean>(false);
    const [initialLoadComplete, setInitialLoadComplete]
    = useState<boolean>(false);
    const [selectedPolygonIndex, setSelectedPolygonIndex] = useState<number>(0);
    // const [tooltipText, setTooltipText] = useState<string>("");

    // ██╗   ██╗ █████╗ ██████╗ ███████╗
    // ██║   ██║██╔══██╗██╔══██╗██╔════╝
    // ██║   ██║███████║██████╔╝███████╗
    // ╚██╗ ██╔╝██╔══██║██╔══██╗╚════██║
    //  ╚████╔╝ ██║  ██║██║  ██║███████║
    //   ╚═══╝  ╚═╝  ╚═╝╚═╝  ╚═╝╚══════╝
    const polygonNames = polygons.map((pol: IPolygon) => pol.name);

    const renderType
    = props.renderType === undefined ? RenderType.DEFAULT : props.renderType;
    const { Option } = Select;
    const countyDropdowns = ["County", "DMA", "State", "Zipcode"];

    const title = "Visit Origination";
    // const tooltipText= `Shows the percent of [visitsVisitors] to ${selectedGpsInsightForReportView[0].title} location by home geography [county].`
    const tooltipText = `Shows the percent of ${
        dropdownValue === DropdownComparisonWeight.PRIMARY ? "visitors" : "visits"
    } to ${selectedPolygon} location by home geography ${visitOrigGeogType}.`;
    const initialYValue = "selection1PrimaryWeight";
    const primaryWeightLabel = "Visitors";
    const secondaryWeightLabel = "Visits";
    const scrollableParentSelector = "#GPSReportBody";
    const chartAnim = "zoomIn";
    const makeXLabelsFitDemmit = true;
    const xAxisHeight = 100;
    const ignoreXTickFormatterOnTooltip = true;
    const app = DigitalShareAppType.Mobilytics;
    const itemType = DigitalShareItemType.VisitorOrigination;

    useEffect(() => {
        const polygonIds: { name: string; identifier: string }[] = Array.from(
            polygonNames,
            (n: string, i: number) => ({ name: n, identifier: n.concat(`-${i}`) })
        );

        setPolyIdentifiers(polygonIds);

        const origData = new Map();

        origData.set(polygonIds[0].identifier, storeVisitOrigData);

        const mergedData: IChartMergedDatapoint[] = mergeVisitOrigGPSData({
            selection1: origData.get(polygonIds[0].identifier),
            selection2: null,
            national: null,
        });

        setMergedVisitOrigGPSData(mergedData?.sort(greatestSel1ValueSort));

        if (benchmarkVisitOrigData) {
            origData.set(polygonIds[1].identifier, benchmarkVisitOrigData);
        }

        setOriginationData(origData);
        setPolygonDataType(polygonIds[0].identifier);
        setSelectedPolygon(polygonIds[0].name);
        setInitialLoadComplete(true);
    }, []);

    useEffect(() => {
        if (originationData.has(polygonDataType)) {
            setMergedVisitOrigGPSData(
                mergeVisitOrigGPSData({
                    selection1: originationData.get(polygonDataType),
                    selection2: null,
                    national: null,
                })?.sort(greatestSel1ValueSort)
            );
        }
    }, [visitOrigGeogType, polygonDataType]);

    const mergeVisitOrigGPSData = (
        data: IChartCompare
    ): IChartMergedDatapoint[] => {
        return data?.selection1
            ? (
          data.selection1[visitOrigGeogType] as Array<IGPSChartMergedDatapoint>
            ).map((item: IGPSChartMergedDatapoint) => {
                return {
                    id: item.id,
                    nationalWeight: null,
                    selection1PrimaryWeight: item.visitorWeight
                        ? item.visitorWeight
                        : item.weight,
                    selection1SecondaryWeight: item.visitWeight,
                };
            })
            : null;
    };

    const handleVisitOrigGeogTypeChange = (str: string) => {
        setVisitOrigGeogType(str);
    };

    const handlePolygonDataChange = (value: string) => {
        const index = Number(value);
        setSelectedPolygonIndex(index);

        const poly = polygonIdentifiers[index];
        setPolygonDataType(poly.identifier);
        setSelectedPolygon(poly.name);
    };

    const handleVisitsChange = (value: DropdownComparisonWeight) => {
        switch (value) {
            case DropdownComparisonWeight.PRIMARY:
                setBar1ValueAttr(`selection1PrimaryWeight`);
                setDropdownValue(DropdownComparisonWeight.PRIMARY);
                break;
            case DropdownComparisonWeight.SECONDARY:
                setBar1ValueAttr(`selection1SecondaryWeight`);
                setDropdownValue(DropdownComparisonWeight.SECONDARY);
                break;
        }
    };

    // ERY: gettin' pretty frail...lots of exceptions
    const greatestSel1ValueSort = (
        a: IChartMergedDatapoint,
        b: IChartMergedDatapoint
    ) => {
        if (bar1ValueAttr === `selection1PrimaryWeight`) {
            return b.selection1PrimaryWeight - a.selection1PrimaryWeight;
        } else if (bar1ValueAttr === `selection1SecondaryWeight`) {
            return b.selection1SecondaryWeight - a.selection1SecondaryWeight;
        }
    };

    const InteractionArea: JSX.Element
    = renderType === RenderType.DIGITAL_SHARE
    || renderType === RenderType.PRINT ? null : (
            <ChartButtonWrapper>
                <div>
                    <MobilyticsPolygonSelect
                        benchmarkItems={props.benchmarkItems}
                        selectedIndex={selectedPolygonIndex}
                        onChange={handlePolygonDataChange}
                    />
                    <Select
                        defaultValue={dropdownValue}
                        value={dropdownValue}
                        onChange={handleVisitsChange}
                    >
                        <Option value={DropdownComparisonWeight.PRIMARY}>Visitors</Option>
                        <Option value={DropdownComparisonWeight.SECONDARY}>Visits</Option>
                    </Select>
                    <Select
                        key="geogtype-dropdown"
                        defaultValue={countyDropdowns[3]}
                        onChange={handleVisitOrigGeogTypeChange}
                        style={{ marginLeft: "10px" }}
                    >
                        {countyDropdowns.map((str, i) => (
                            <Option
                                key={`geogtype-dropdownVal-${i}-${str}`}
                                value={str.toLowerCase()}
                            >
                                {shortenStr(str, 20)}
                            </Option>
                        ))}
                    </Select>
                </div>
            </ChartButtonWrapper>
        );

    const ref = useRef<HTMLDivElement>(null);

    const Chart: JSX.Element = mergedVisitOrigGPSData && (
        <div
            data-testclass={"report-chart-group-wrapper"}
            onClick={() => {
                setVisibleModal(true);
            }}
        >
            <RechartBarLine
                initialYValue={initialYValue}
                yValueTickFormatter={formatYValue}
                xTickFormatter={formatLabelWithEllipsis}
                bar1color={
                    selectedPolygon === polygons[0].name ? colorTwoBright : colorTwoDark
                }
                data={mergedVisitOrigGPSData}
                bar1ValueAttr={bar1ValueAttr}
                makeXLabelsFitDemmit={makeXLabelsFitDemmit}
                xAxisHeight={xAxisHeight}
                primaryWeightLabel={primaryWeightLabel}
                secondaryWeightLabel={secondaryWeightLabel}
                ignoreXTickFormatterOnTooltip={ignoreXTickFormatterOnTooltip}
                disableAnimation={renderType !== RenderType.DEFAULT}
            />
        </div>
    );

    if (
        featureFlags.DigitalShare
    && renderType === RenderType.DIGITAL_SHARE
    && isOutputSelected(app, itemType)
    ) {
        if (initialLoadComplete) {
            registerRef(app.toString(), itemType.toString(), ref);
            registerElement(app.toString(), itemType.toString(), tooltipText, Chart);
        }
    }

    return (
        <ReportDataWrapper data-testclass="report-chart-group-wrapper">
            <ChartTitle titleText={title} />
            <p>{tooltipText}</p>
            <ChartBodyWrapper
                scrollableParentSelector={scrollableParentSelector}
                animateIn={chartAnim}
                offset={100}
                animateOnce={true}
            >
                {initialLoadComplete && mergedVisitOrigGPSData && InteractionArea}
                {initialLoadComplete && mergedVisitOrigGPSData && Chart}
            </ChartBodyWrapper>
        </ReportDataWrapper>
    );
};
